import React, { useState, useEffect } from 'react'
import { host } from '../script/variables'

const API_URL = host

const categories = [
  { key: 'OverallExperienceRating', label: 'Overall Experience' },
  { key: 'FoodQualityRating',       label: 'Food Quality'       },
  { key: 'FoodPricingRating',       label: 'Value for Money'    },
  { key: 'StaffBehaviourRating',    label: 'Staff Behaviour'    },
  { key: 'CleaninessRating',        label: 'Cleanliness'        },
]

/* ── Star row ── */
const StarRow = ({ label, value, onChange }) => {
  const [hovered, setHovered] = useState(0)

  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <div style={styles.stars}>
        {[1, 2, 3, 4, 5].map(n => {
          const filled = n <= (hovered || value)
          return (
            <button
              key={n}
              type="button"
              aria-label={`Rate ${n} out of 5`}
              onMouseEnter={() => setHovered(n)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => onChange(n)}
              style={{
                ...styles.star,
                color: filled ? '#1c1c1c' : '#d4d4d4',
                transform: hovered === n ? 'scale(1.18)' : 'scale(1)',
              }}
            >
              ★
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ── Main component ── */
const ResFeedback = () => {
  const params     = new URLSearchParams(window.location.search)
  const orderId    = params.get('orderid')

  const [ratings, setRatings] = useState({
    OverallExperienceRating : 0,
    FoodQualityRating       : 0,
    FoodPricingRating       : 0,
    StaffBehaviourRating    : 0,
    CleaninessRating        : 0,
  })
  const [comment,     setComment]     = useState('')
  const [status,      setStatus]      = useState('idle')   // idle | loading | success | error
  const [errorMsg,    setErrorMsg]    = useState('')
  const [restaurantName, setRestaurantName] = useState('')

  /* Optionally fetch restaurant name from order */
//   useEffect(() => {
//     if (!orderId) return
//     fetch(`${API_URL}/resfeedback/order-info?orderid=${orderId}`)
//       .then(r => r.ok ? r.json() : null)
//       .then(d => { if (d?.restaurantName) setRestaurantName(d.restaurantName) })
//       .catch(() => {})
//   }, [orderId])

  const setRating = (key, val) =>
    setRatings(prev => ({ ...prev, [key]: val }))

  const allRated = Object.values(ratings).every(v => v > 0)

  const handleSubmit = async () => {
    if (!orderId)  return setErrorMsg('Invalid feedback link.')
    if (!allRated) return setErrorMsg('Please rate all categories before submitting.')

    setStatus('loading')
    setErrorMsg('')

    try {
      const res = await fetch(`${API_URL}/feedback/submit-form`, {
        method  : 'POST',
        headers : { 'Content-Type': 'application/json' },
        body    : JSON.stringify({ orderId, ...ratings, FeedbackComment: comment }),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data?.message || 'Something went wrong.')
      setStatus('success')
    } catch (err) {
      setErrorMsg(err.message)
      setStatus('idle')
    }
  }

  /* ── Success screen ── */
  if (status === 'success') {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, textAlign: 'center', padding: '60px 40px' }}>
          <div style={styles.checkmark}>✓</div>
          <p style={styles.successTitle}>Thank you!</p>
          <p style={styles.successSub}>Your feedback has been saved.</p>
        </div>
      </div>
    )
  }

  /* ── Main form ── */
  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <p style={styles.eyebrow}>Your visit · Yesterday</p>
          <h1 style={styles.title}>
            {restaurantName || 'How was your visit?'}
          </h1>
          {restaurantName && (
            <p style={styles.subtitle}>How was your experience?</p>
          )}
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Star rows */}
        <div style={styles.ratingBlock}>
          {categories.map(c => (
            <StarRow
              key={c.key}
              label={c.label}
              value={ratings[c.key]}
              onChange={val => setRating(c.key, val)}
            />
          ))}
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Comment */}
        <div style={styles.commentBlock}>
          <label style={styles.commentLabel} htmlFor="feedback-comment">
            Anything else to add? <span style={{ color: '#aaa' }}>(optional)</span>
          </label>
          <textarea
            id="feedback-comment"
            rows={4}
            placeholder="Tell us what you loved or what we can improve…"
            value={comment}
            onChange={e => setComment(e.target.value)}
            style={styles.textarea}
          />
        </div>

        {/* Error */}
        {errorMsg && <p style={styles.error}>{errorMsg}</p>}

        {/* Submit */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={status === 'loading'}
          style={{
            ...styles.btn,
            opacity: status === 'loading' ? 0.6 : 1,
            cursor : status === 'loading' ? 'not-allowed' : 'pointer',
          }}
        >
          {status === 'loading' ? 'Saving…' : 'Submit Feedback'}
        </button>

        <p style={styles.hint}>Takes 30 seconds · No account needed</p>
      </div>
    </div>
  )
}

/* ── Styles ── */
const styles = {
  page: {
    minHeight       : '100vh',
    background      : '#f5f3ef',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    padding         : '40px 16px',
    fontFamily      : 'Georgia, serif',
  },
  card: {
    background    : '#ffffff',
    borderRadius  : '18px',
    padding       : '44px 40px',
    width         : '100%',
    maxWidth      : '480px',
    boxShadow     : '0 4px 40px rgba(0,0,0,.08)',
  },
  header: {
    marginBottom: '28px',
  },
  eyebrow: {
    margin       : '0 0 8px',
    fontSize     : '11px',
    color        : '#aaa',
    letterSpacing: '.15em',
    textTransform: 'uppercase',
  },
  title: {
    margin    : '0 0 4px',
    fontSize  : '26px',
    fontWeight: 'normal',
    color     : '#1c1c1c',
    lineHeight: 1.3,
  },
  subtitle: {
    margin  : '4px 0 0',
    fontSize: '14px',
    color   : '#888',
  },
  divider: {
    borderTop: '1px solid #f0f0f0',
    margin   : '24px 0',
  },
  ratingBlock: {
    display      : 'flex',
    flexDirection: 'column',
    gap          : '4px',
  },
  row: {
    display       : 'flex',
    alignItems    : 'center',
    justifyContent: 'space-between',
    padding       : '10px 0',
    borderBottom  : '1px solid #f7f7f7',
  },
  rowLabel: {
    fontSize: '13px',
    color   : '#555',
  },
  stars: {
    display: 'flex',
    gap    : '2px',
  },
  star: {
    background  : 'none',
    border      : 'none',
    fontSize    : '22px',
    cursor      : 'pointer',
    padding     : '0 2px',
    lineHeight  : 1,
    transition  : 'color .12s, transform .12s',
    userSelect  : 'none',
  },
  commentBlock: {
    display      : 'flex',
    flexDirection: 'column',
    gap          : '8px',
    marginBottom : '24px',
  },
  commentLabel: {
    fontSize: '13px',
    color   : '#555',
  },
  textarea: {
    width        : '100%',
    padding      : '12px 14px',
    border       : '1px solid #e8e8e8',
    borderRadius : '10px',
    fontSize     : '13px',
    fontFamily   : 'Georgia, serif',
    color        : '#1c1c1c',
    resize       : 'vertical',
    outline      : 'none',
    boxSizing    : 'border-box',
    lineHeight   : 1.7,
    background   : '#fafafa',
  },
  error: {
    fontSize    : '12px',
    color       : '#c0392b',
    margin      : '0 0 16px',
    textAlign   : 'center',
  },
  btn: {
    width        : '100%',
    padding      : '14px',
    background   : '#1c1c1c',
    color        : '#fff',
    border       : 'none',
    borderRadius : '50px',
    fontSize     : '14px',
    fontFamily   : 'Georgia, serif',
    letterSpacing: '.02em',
    transition   : 'opacity .2s',
  },
  hint: {
    margin   : '12px 0 0',
    fontSize : '11px',
    color    : '#bbb',
    textAlign: 'center',
  },
  checkmark: {
    width       : '52px',
    height      : '52px',
    borderRadius: '50%',
    background  : '#1c1c1c',
    color       : '#fff',
    fontSize    : '22px',
    display     : 'flex',
    alignItems  : 'center',
    justifyContent: 'center',
    margin      : '0 auto 20px',
  },
  successTitle: {
    margin  : '0 0 6px',
    fontSize: '22px',
    color   : '#1c1c1c',
  },
  successSub: {
    margin  : 0,
    fontSize: '14px',
    color   : '#888',
  },
}

export default ResFeedback